import { Toast } from 'primereact/toast';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { clearNotification } from '../../redux/slices/notification.slice';
import { useAppDispatch, useAppSelector } from '../../redux/stateHooks';

const NOTIFICATION_LIFE = 5000; // default 10 seconds for improved readability

// Maps a PrimeReact severity to its localized title (key + English default).
const SEVERITY_TITLE: Record<string, { key: string; def: string }> = {
  success: { key: 'common.success', def: 'Success' },
  info: { key: 'common.info', def: 'Info' },
  warn: { key: 'common.warning', def: 'Warning' },
  error: { key: 'common.error', def: 'Error' },
};

export const GlobalNotifier = () => {
  const toast = useRef<Toast>(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { message, severity, key, life } = useAppSelector((state) => state.notification);

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    if (message && toast.current) {
      const lifeMs = typeof life === 'number' && life > 0 ? life : NOTIFICATION_LIFE;

      // Localize the toast title; fall back to capitalised severity for any
      // unmapped value so a new severity never renders blank.
      const title = SEVERITY_TITLE[severity];
      const summary = title
        ? t(title.key, title.def)
        : severity.charAt(0).toUpperCase() + severity.slice(1);

      toast.current.show({
        severity: severity,
        summary,
        detail: message,
        life: lifeMs,
      });

      timerId = setTimeout(() => {
        dispatch(clearNotification());
      }, lifeMs + 500);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [message, severity, key, life]);

  // Ensure Toast appears above modals by using a high baseZIndex
  return <Toast ref={toast} baseZIndex={99999} />;
};
